import { useCallback, useMemo, useState } from 'react';

/**
 * Toolbar navigation history — a small state machine layered on top of the
 * existing (domain, sub-feature) selection. It is purely presentational: it
 * records where the user has been in the feature bar and lets Back/Forward
 * walk that history, without changing how a selection ultimately drives
 * `vehicleTab` / `vehicleSubTab` (that stays the caller's job).
 *
 * Two kinds of view live in the stack:
 *  - `expanded`  → the normal full bar (domain dropdown + every sub-tab pill).
 *  - `focused`   → a single feature pill has been clicked; it now occupies the
 *                  domain slot and the rest of the bar goes blank (Marketplace
 *                  is rendered by the toolbar regardless and is not part of a
 *                  view).
 *
 * History semantics (browser-style):
 *  - The stack always starts with a single `expanded` entry — the initial
 *    "Select domain" state and the deepest Back target.
 *  - Clicking a real feature pill pushes a `focused` entry, truncating any
 *    forward history (a new branch replaces the old forward path).
 *  - Back moves the cursor toward 0; Forward moves it toward the end.
 *  - `canBack` is false at the start of the stack; `canForward` is false at the
 *    end. The toolbar greys the matching button out when these are false.
 */

export interface ExpandedView {
  kind: 'expanded';
}

export interface FocusedView {
  kind: 'focused';
  /** The sub-tab value that was clicked and now owns the domain slot. */
  subTab: string;
}

export type ToolbarView = ExpandedView | FocusedView;

const EXPANDED: ExpandedView = { kind: 'expanded' };

export interface ToolbarNav {
  /** The view the toolbar should render right now. */
  current: ToolbarView;
  canBack: boolean;
  canForward: boolean;
  /** Push a focused view for a clicked feature pill (truncates forward). */
  focus: (subTab: string) => void;
  /**
   * Reset the whole stack to a single expanded entry. Used when the domain
   * selection changes out from under the history (picking a new domain in the
   * dropdown starts a fresh browsing session).
   */
  reset: () => void;
  back: () => void;
  forward: () => void;
}

export function useToolbarNav(): ToolbarNav {
  const [stack, setStack] = useState<ToolbarView[]>([EXPANDED]);
  const [cursor, setCursor] = useState(0);

  const focus = useCallback(
    (subTab: string) => {
      setStack((prev) => {
        // Truncate any forward history, then push the focused view.
        const trimmed = prev.slice(0, cursor + 1);
        return [...trimmed, { kind: 'focused', subTab }];
      });
      setCursor((c) => c + 1);
    },
    [cursor],
  );

  const reset = useCallback(() => {
    setStack([EXPANDED]);
    setCursor(0);
  }, []);

  const back = useCallback(() => {
    setCursor((c) => (c > 0 ? c - 1 : c));
  }, []);

  const forward = useCallback(() => {
    setCursor((c) => c + 1);
  }, []);

  // Clamp the forward cursor to the stack length in case a focus() truncation
  // left it dangling (defensive — cursor is always kept in-range by the setters
  // above, but this keeps `current` total).
  const safeCursor = Math.min(cursor, stack.length - 1);
  const current = stack[safeCursor] ?? EXPANDED;

  return useMemo(
    () => ({
      current,
      canBack: safeCursor > 0,
      canForward: safeCursor < stack.length - 1,
      focus,
      reset,
      back,
      forward,
    }),
    [current, safeCursor, stack.length, focus, reset, back, forward],
  );
}
